// global-css-imports start
//  note: if you are modifying the CSS on these blocks, please also modify it on .storybook/preview.tsx

// CSS library import
import '@speechifyinc/voice-cloning-web/dist/index.css';
import 'nprogress/nprogress.css';
import 'react-loading-skeleton/dist/skeleton.css';
import 'simplebar-react/dist/simplebar.min.css';

// local CSS import
import 'css/animations.css';
import 'css/askai.css';
import 'css/experience.css';
import 'css/fonts-antialiasing.css';
import 'css/fonts.css';
import 'css/main.css';
import 'css/normalize.css';
import 'css/tailwind.css';
import 'css/theme-switch-animation.css';
import 'css/three-dots.min.css';

import '../i18n';

import { AppProps } from 'next/app';
import { useEffect, useState } from 'react';

import { PageLoader } from 'components/common/loaders/PageLoader';
import { FullScreenPromptProvider } from 'components/mobileAppPrompts/FullScreenPromptProvider';
import useTrackRouteVisit, { getIsInitialRoute } from 'hooks/useTrackRouteVisit';
import withFaro from 'lib/observability/withFaro';
import { awaitStoresHydration } from 'lib/zustand/store/registry';
import { useAuthStore } from 'modules/auth/store/authStore';
import { initIsCtrlPressedState } from 'modules/listening/features/openingLinks/isCtrlPressedState';
import { loadingStoreActions } from 'modules/listening/stores/loadingStore';
import { initAnalytics } from 'utils/analytics';
import { initMessageListener } from 'utils/extension';
import MainWrapper from 'wrappers/MainWrapper';

// global-css-imports end

// Global store hydration provider
const GlobalStoreHydrationProvider = ({ children }: { children: React.ReactNode }) => {
  const [isHydrated, setIsHydrated] = useState(false);

  useEffect(() => {
    async function waitForInitialHydration() {
      try {
        await awaitStoresHydration();
        setIsHydrated(true);
      } catch (error) {
        console.warn('Failed to hydrate some stores:', error);
        // Still set as hydrated to prevent infinite loading
        setIsHydrated(true);
      }
    }

    waitForInitialHydration();
  }, []);

  if (!isHydrated) {
    return <PageLoader isLoading className="bg-bg-prim-w-b" />;
  }

  return <>{children}</>;
};

// App content component that uses stores after hydration
const AppContent = ({ Component, pageProps }: { Component: AppProps['Component']; pageProps: AppProps['pageProps'] }): JSX.Element => {
  const isLoggingOut = useAuthStore(s => s.isLoggingOut);

  useEffect(() => {
    initAnalytics();
    initIsCtrlPressedState();
    const removeMessageListener = initMessageListener();
    return () => {
      removeMessageListener();
    };
  }, []);

  useEffect(() => {
    if (getIsInitialRoute()) {
      loadingStoreActions.setIsLongLoadTimeExpected(true);
    }
  }, []);

  useTrackRouteVisit();

  if (isLoggingOut) {
    return <PageLoader isLoading className="bg-bg-prim-w-b" />;
  }

  return (
    <MainWrapper>
      <FullScreenPromptProvider>
        <Component {...pageProps} />
      </FullScreenPromptProvider>
    </MainWrapper>
  );
};

const App = ({ Component, pageProps }: AppProps): JSX.Element => {
  return (
    <GlobalStoreHydrationProvider>
      <AppContent Component={Component} pageProps={pageProps} />
    </GlobalStoreHydrationProvider>
  );
};

export default withFaro(App);
